<template>
  <div :class="direction">
    <Navbar  v-if="!$route.meta.hideNavbar"/>
    <router-view />
    <Footer v-if="!$route.meta.hideFooter"/>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import Navbar from "@/components/layout/main-navbar.vue";
import Footer from "@/components/layout/main-footer.vue";
import { useLanguageStore } from "@/store/languageStore";
import LayoutService from "@/services/layout";
import handleError from "@/helper/handleError";
import { useRoute } from "vue-router";

export default defineComponent({
  components: {
    Navbar,
    Footer,
  },
  setup() {
    const languageStore = useLanguageStore();
    const fontFamily = computed(() => generalData.value.font_type ||"Vazirmatn, sans-serif");
    const primaryColor = computed(() => generalData.value.default_color || "#007BF8");
    const metaTitle =  computed(() => generalData.value.product_name || "Product page");
    const direction = computed(() => languageStore.direction);
    const generalData = ref({
      product_name: '',
      default_color: '',
      font_type: ''
    });
    const route = useRoute();
    // Resolve reactively, and prefer the URL over localStorage:
    //  - vue-router's initial navigation is async (install() pushes without awaiting),
    //    so route.query is still empty while App's setup() runs. Reading it once here
    //    left productID undefined on any direct link, and the title never loaded.
    //  - localStorage holds whatever product was opened last, which is not necessarily
    //    the one in ?productID=. PoliciesView already prefers the query; match it, or
    //    the head title names a different product than the page body.
    const productID = computed(
      () => (route.query.productID as string) || localStorage.getItem("productID") || ""
    );
    // ======================== Methods ===================
    const getSettings = () => {
      if (!productID.value) return;
      LayoutService.getSettings(productID.value)
        .then((response) => {
          generalData.value = response.data.data;
        })
        .catch((err: any) => {
          handleError(err);
        });
    };
    // ======================= Load settings ==================
    watch(productID, getSettings, { immediate: true });

// ============================ Watch ======================
    // Watch for changes in the fontFamily and primaryColor and apply them dynamically
    watch(
  [fontFamily, primaryColor],
  ([newFontFamily, newPrimaryColor]) => {
    if (newFontFamily) {
      // Set CSS variable
      document.documentElement.style.setProperty(
        "--font-family",
        newFontFamily
      );
      
      // Remove any previously injected Google Font links
      const existingFontLinks = document.querySelectorAll('link[data-google-font]');
      existingFontLinks.forEach(link => link.remove());
      
      // Load the font from Google Fonts if it's not a system font
      if (!newFontFamily.includes('sans-serif') && !newFontFamily.includes('serif') && 
          !newFontFamily.includes('monospace') && !newFontFamily.includes('Arial') && 
          !newFontFamily.includes('Helvetica') && !newFontFamily.includes('Times')) {
        
        // Extract the font name before any commas
        const fontName = newFontFamily.split(',')[0].trim();
        
        // Create Google Fonts link
        const link = document.createElement('link');
        link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@400;700&display=swap`;
        link.rel = 'stylesheet';
        link.setAttribute('data-google-font', 'true');
        document.head.appendChild(link);
      }
    }

    if (newPrimaryColor) {
      document.documentElement.style.setProperty(
        "--primary-color",
        newPrimaryColor
      );
    }
  },
  { immediate: true }
);

    //  watch for title
    watch(
  () => [metaTitle.value],
  ([newMetaTitle]) => {

    if (newMetaTitle) {
      document.title = newMetaTitle;
      const metaTitleTag =
        document.querySelector("meta[property='og:title']") ||
        document.createElement("meta");
      metaTitleTag.setAttribute("property", "og:title");
      metaTitleTag.setAttribute("content", newMetaTitle);
      document.head.appendChild(metaTitleTag);
    }
  },
  { immediate: true }
);

// watch(
//       () => localStorage.getItem('productID'),
//       (newProductID) => {
//         if (newProductID) {
//           productID = newProductID as string;
//           getSettings();
//         }
//       },
//       { immediate: true } // This ensures it runs immediately if the productID is already available
//     );

    return {
      direction,
      primaryColor,
      fontFamily, 
      getSettings,
      productID
    };
  },
});
</script>

<style lang="scss">
#app {
  font-family: var(--font-family, Avenir, Helvetica, Arial, sans-serif);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
</style>
